import React from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { range } from 'utils/commonUtils'
import { OptionItem } from '../common'
import DrumPicker from '../items/DrumPicker'
import AnimationModal from 'components/elements/modal/AnimationModal'

// 変形フォーマットプラグインを追加
dayjs.extend(advancedFormat)

export type TimePickerProps = {
  /** 時間(HH:mm) */
  time: string
  /** 選択可能な時間の最小値(HH:mm) */
  minTime?: string
  /** 値が変更された時のコールバック */
  onChanged?: (time: string) => void
}

/** 時間の選択肢 */
const hourOptions: OptionItem<number>[] = range(0, 23).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))
/** 分の選択肢 */
const minuteOptions: OptionItem<number>[] = range(0, 59).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))

/** 時間を選択するコンポーネント */
const TimePicker = (props: TimePickerProps): JSX.Element => {
  const { time, minTime, onChanged } = props
  const [isPickerOpened, setIsPickerOpened] = React.useState(false)
  const [selectedHour, setSelectedHour] = React.useState(0)
  const [selectedMinute, setSelectedMinute] = React.useState(0)
  const [hourItems, setHourItems] = React.useState<OptionItem<number>[]>([])
  const [minuteItems, setMinuteItems] = React.useState<OptionItem<number>[]>([])

  // 時間
  const [hour, minute] = React.useMemo(() => {
    const djTime = dayjs(`2000/1/1 ${time}`)
    return [djTime.hour(), djTime.minute()]
  }, [time])

  // 最小時間
  const [minHour, minMinute] = React.useMemo(() => {
    const djTime = minTime ? dayjs(`2000/1/1 ${minTime}`) : undefined
    return djTime ? [djTime.hour(), djTime.minute()] : [0, 0]
  }, [minTime])

  /** 予約開始時間を押下したときのイベント */
  const handleTimeClick = React.useCallback(
    (value?: boolean) => {
      const currentMinMinute = minHour === hour ? minMinute : 0

      // ドラムの選択肢を設定
      setHourItems(hourOptions.filter((x) => x.value >= minHour))
      setMinuteItems(minuteOptions.filter((x) => x.value >= currentMinMinute))
      // ドラムの選択値を設定
      setSelectedHour(selectedHour <= minHour ? minHour : selectedHour)
      setSelectedMinute(
        selectedMinute <= minMinute ? minMinute : selectedMinute
      )
      // モーダル表示
      value && setIsPickerOpened(true)
    },
    [hour, minute, minHour, minMinute, setHourItems, setMinuteItems]
  )

  /** 完了ボタン押下時のイベント */
  const handleComplete = React.useCallback(() => {
    // モーダル非表示
    setIsPickerOpened(false)
    if (onChanged) {
      // 変更通知
      onChanged(
        `${String(selectedHour).padStart(2, '0')}:${String(
          selectedMinute
        ).padStart(2, '0')}`
      )
    }
  }, [selectedHour, selectedMinute, onChanged])

  /** 予約時間を切り替えたときのイベント */
  const handleHourChanged = React.useCallback(
    (value: number) => {
      if (minHour || minMinute) {
        // 最小時間の指定あり
        if (value === minHour) {
          // 最小時間に変更された場合は分を最小値にして選択肢を絞る
          setMinuteItems(minuteOptions.filter((x) => x.value >= minMinute))
          setSelectedMinute(minMinute)
        } else if (selectedHour === minHour) {
          // 最小時間からの変更の場合は分の選択肢を全てにする
          setMinuteItems(minuteOptions)
        }
      }
      // 時間変更
      setSelectedHour(value)
    },
    [minHour, minMinute, selectedHour]
  )

  const handleMinuteChanged = React.useCallback(
    (value: number) => {
      setSelectedMinute(value)
    },
    [setSelectedMinute]
  )
  // 日付を切り替えたときにTimeの選択肢を生成する
  React.useLayoutEffect(() => {
    if (minTime) {
      const currentMinHour = minHour > selectedHour ? minHour : selectedHour
      const currentMinMinute =
        minMinute > selectedMinute ? minMinute : selectedMinute
      props.onChanged &&
        props.onChanged(
          `${String(currentMinHour).padStart(2, '0')}:${String(
            currentMinMinute
          ).padStart(2, '0')}`
        )
      return
    }
    handleTimeClick(false)
  }, [minTime, handleTimeClick])

  return (
    <>
      <TimeContainer
        onClick={(): void => {
          handleTimeClick(true)
        }}
      >
        {time}
      </TimeContainer>
      <AnimationModal
        animationType="transparent"
        on={isPickerOpened}
        type="manual"
        manualBottom="0px"
        scrollLock={true}
      >
        <ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleComplete}>完了</ModalButton>
          </ModalButtonContainer>
          <TimePickerContainer>
            <DrumPicker
              options={hourItems}
              selectedValue={selectedHour}
              width={50}
              height={120}
              onValueChanged={handleHourChanged}
            />
            <DrumPicker
              options={minuteItems}
              selectedValue={selectedMinute}
              width={50}
              height={120}
              onValueChanged={handleMinuteChanged}
            />
          </TimePickerContainer>
        </ModalContainer>
      </AnimationModal>
    </>
  )
}

export default React.memo(TimePicker)

/** ルートのスタイル定義 */
const TimeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 114px;
  height: 53px;
  padding: 4px 8px;
  font-weight: bold;
  font-size: 18px;
  border: 1px solid #707070;
`

/** モーダルのスタイル定義(ルート) */
const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
`

/** 完了ボタンのスタイル定義(ルート) */
const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 23px;
  border: #707070 1px solid;
`

/** 完了ボタンのスタイル定義 */
const ModalButton = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 8px;
  color: #1492e6;
  font-weight: bold;
  cursor: pointer;
`

/** 時/分を入力するエリアのスタイル定義 */
const TimePickerContainer = styled.div`
  display: flex;
  flex: 1;
  padding: 16px 0px;
  background: #abb0ba;
  border: #707070 1px solid;
`
